import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getBranchDetails } from "@/service/reqUrl.service";

import type { BranchDetailsParams } from "./types";

interface UseBranchDetailsResult {
  /** Raw SP document (`{ MetricBranch: [...] }` or a bare array), kept unshaped
   *  — the table normalizes. */
  data: unknown;
  loading: boolean;
  error: string | null;
}

/**
 * Fires `POST /dashboard/branch-details` for the By-Branch table. Independent of
 * the dashboard-metrics call: it goes out as soon as its params (dashboard id +
 * service line) are known, so it never blocks the initial render, and re-fires
 * whenever the date range or any filter in `params` changes. `params` is null
 * until the dashboard id resolves.
 *
 * Errors render inline in the table's own section — no toast, as before.
 */
export function useBranchDetails(
  params: BranchDetailsParams | null,
): UseBranchDetailsResult {
  const userKey = useUserKey();

  const { data, loading, error } = useApiQuery<unknown>({
    queryKey: qk.branchDetails(userKey, params),
    url: getBranchDetails,
    body: params,
    enabled: params != null,
    fallbackMessage: "Failed to fetch branch details",
  });

  return { data, loading, error };
}
